import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { unavailableReasonProps } from "/imports/libs/unavailableReason";
import { DataView, EmptyState, PickerRulesPanel, ServiceAvailabilityNotice } from "/imports/ui/components/shared";
import { TimeSlotCard, SLOT_SECTION_LABEL_SX, getUniformGridPrice, SUGGESTED_BUCKET_ID, PackComposerTray } from "/imports/ui/components/booking";
import DurationPrompt from "./components/DurationPrompt";
import CourtMobileFilterBar from "./components/CourtMobileFilterBar";
import CourtFilterSheet from "./components/CourtFilterSheet";
import CourtRescheduleBanner from "./components/CourtRescheduleBanner";
import CourtRescheduleDialog from "./components/CourtRescheduleDialog";
import { rescheduleEmptyProps } from "./courtHelpers";

// Mobile layout — sticky filter chip bar + sticky date strip + segmented slot
// list. Filter and multi-option pickers live in bottom sheets.
function CourtsMobileLayout({ court, scrollSpy }) {
  const { navigate, slug, currency, filters, setFilter, setSelectedSlot, loading, error, availability, bucketedSlots, suggestedGroups } = court;
  const { sectionRefs, sectionOffset } = scrollSpy;
  // A dated closure / time off emptied the day: say so, and offer the lever
  // the client actually has (drop the resource filter) rather than the date.
  const closureProps = unavailableReasonProps({
    closureReasons: availability?.closureReasons,
    canClearResource: !!filters.facilityId && court.allowCourtPick !== false,
    onClearResource: () => {
      setFilter("facilityId", null);
      setSelectedSlot(null);
    },
    onNextDate: () => {
      setFilter("date", court.tomorrowISO);
      setSelectedSlot(null);
    }
  });

  // One shared price across the whole day → drop it from every cell (the
  // duration picker already states it); varying prices stay per-cell.
  const uniformDayPrice = getUniformGridPrice(bucketedSlots.flatMap((b) => b.groups));

  const packMode = court.pack?.active;
  const isRescheduling = court.reschedule?.active;

  const renderSlotCard = (group) => {
    const isMulti = group.slots.length > 1;
    return (
      <TimeSlotCard
        key={group.time}
        time={group.time}
        price={group.minPrice}
        currency={group.currency || currency}
        selected={packMode && court.pack.isPicked(filters.date, group.time)}
        onClick={() => court.handleGroupClick(group)}
        optionsLabel={court.getOptionsLabel(group.slots)}
        pricePrefix={isMulti ? "from" : null}
        hidePrice={packMode || uniformDayPrice != null}
        dense
      />
    );
  };

  return (
    <Box sx={{ pb: packMode ? { xs: "11rem", md: "7rem" } : 0 }}>
      {/* Compact hero */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
        <IconButton size="small" onClick={court.goBack} aria-label="Go back" sx={{ ml: -0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
          {packMode ? "Pick your pack sessions" : isRescheduling ? "Move your court booking" : "Reserve Your Court"}
        </Typography>
      </Box>

      {isRescheduling && <CourtRescheduleBanner reschedule={court.reschedule} currency={currency} sx={{ mb: 2 }} />}

      {/* The club's rules for buying a pack, in the one place the client can still
          act on them. Only while composing a pack — an ordinary booking has no
          pack rules to obey. */}
      {packMode && <PickerRulesPanel title="Before you buy" rules={court.pack.packRules} />}

      <CourtMobileFilterBar court={court} scrollSpy={scrollSpy} />

      {/* Slots — all buckets stacked in one scroll, each section anchored so
          tapping a segment (or scrolling) keeps the nav in sync. */}
      <Box mt={2}>
        {!filters.duration ? (
          <DurationPrompt />
        ) : (
          <DataView loading={loading} error={error} data={availability} loadingMessage="Loading availability...">
            {bucketedSlots.length > 0 ? (
              <Box>
                {suggestedGroups.length > 0 && (
                  <Box
                    data-bucket={SUGGESTED_BUCKET_ID}
                    ref={(el) => {
                      sectionRefs.current[SUGGESTED_BUCKET_ID] = el;
                    }}
                    sx={{ scrollMarginTop: `${sectionOffset}px`, mb: 3.5 }}
                  >
                    <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>Suggested for you · {suggestedGroups.length}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25 }}>{suggestedGroups.map(renderSlotCard)}</Box>
                  </Box>
                )}
                {bucketedSlots.map((bucket) => (
                  <Box
                    key={bucket.id}
                    data-bucket={bucket.id}
                    ref={(el) => {
                      sectionRefs.current[bucket.id] = el;
                    }}
                    sx={{ scrollMarginTop: `${sectionOffset}px`, mb: 3.5 }}
                  >
                    <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>
                      {bucket.label} · {bucket.groups.length}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25 }}>{bucket.groups.map(renderSlotCard)}</Box>
                  </Box>
                ))}
              </Box>
            ) : closureProps ? (
              <EmptyState {...closureProps} />
            ) : availability?.availabilityInfo?.length > 0 ? (
              <ServiceAvailabilityNotice
                availabilityInfo={availability.availabilityInfo}
                actionLabel="Try Tomorrow"
                onAction={() => {
                  setFilter("date", court.tomorrowISO);
                  setSelectedSlot(null);
                }}
              />
            ) : isRescheduling ? (
              <EmptyState
                {...rescheduleEmptyProps({
                  onNext: () => {
                    setFilter("date", court.tomorrowISO);
                    setSelectedSlot(null);
                  }
                })}
              />
            ) : (
              <EmptyState
                title="No availability"
                description="No courts are available for the selected date and filters. Try a different date or adjust your filters."
                actionLabel="Try Tomorrow"
                onAction={() => {
                  setFilter("date", court.tomorrowISO);
                  setSelectedSlot(null);
                }}
              />
            )}
          </DataView>
        )}
      </Box>

      <CourtFilterSheet court={court} />
      <PackComposerTray pack={court.pack} />
      {isRescheduling && <CourtRescheduleDialog reschedule={court.reschedule} currency={currency} isMobile />}
    </Box>
  );
}

export default CourtsMobileLayout;
